import { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Star, Users, CheckCircle2, Zap } from "lucide-react";
import api from "@/lib/api";
import SEO, { SITE_URL } from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import {
  CATEGORY_META,
  ALL_CATEGORY_IDS,
  OVERALL_META,
  prettyCategory,
  composeSeedPage,
} from "./bestListContent";
import { getSeed, siblingSeedSlugs } from "./bestListSeeds";

/**
 * Programmatic SEO landing page.
 *
 * Routes:
 *   /best/discord-servers                  → top 25 servers (all categories)
 *   /best/:categoryId-discord-servers      → top 25 servers in a category
 *   /best/:tag-discord-servers             → top servers for any tag
 *
 * Every landing page:
 *   - has hand-written unique copy per category (see bestListContent.js).
 *   - links to canonical server detail pages (internal linking).
 *   - emits ItemList + FAQPage JSON-LD synchronously on first paint so
 *     Google's raw-HTML pass captures the structured data even before
 *     the API returns the ranked list.
 *   - is listed in the sitemap with a real lastmod from server data.
 */
export default function BestList() {
  const { slug } = useParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const parsed = useMemo(() => parseSlug(slug), [slug]);

  useEffect(() => {
    if (!parsed) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/servers", {
          params: {
            category: parsed.categoryId || undefined,
            tag: parsed.tag || undefined,
            sort: "members",
            limit: 25,
          },
        });
        if (!alive) return;
        setItems(data.items || []);
        setTotal(data.total || 0);
        // Use the most-recently-updated server's timestamp as the "last
        // updated" signal for this list — real freshness, not a fake date.
        const latest = (data.items || [])
          .map((s) => s.updated_at)
          .filter(Boolean)
          .sort()
          .pop();
        setLastUpdated(latest || null);
      } catch {
        if (alive) setItems([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [parsed]);

  if (!parsed) return <Navigate to="/servers" replace />;

  const { title, h1, intro, faq, tips, categoryId, tag, seedSlug, seedType } = parsed;
  const canonicalPath = `/best/${slug}`;

  // Aggregate stats — shown live, but the schema is emitted regardless so
  // the raw HTML always contains structured data (even before API returns).
  const totalMembers = items.reduce(
    (sum, s) => sum + (Number(s.member_count) || 0),
    0
  );
  const rated = items.filter(
    (s) => typeof s.avg_rating === "number" && s.avg_rating > 0
  );
  const avgRating =
    rated.length > 0
      ? rated.reduce((a, s) => a + s.avg_rating, 0) / rated.length
      : null;
  const totalReviews = items.reduce(
    (sum, s) => sum + (Number(s.review_count) || 0),
    0
  );

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: h1,
    description: intro,
    itemListOrder: "https://schema.org/ItemListOrderDescending",
    numberOfItems: items.length,
    itemListElement: items.map((s, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SITE_URL}/servers/${s.slug || s.id}`,
      name: s.name,
    })),
  };
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((qa) => ({
      "@type": "Question",
      name: qa.q,
      acceptedAnswer: { "@type": "Answer", text: qa.a },
    })),
  };
  const schemas = [itemListSchema, faqSchema];
  if (avgRating && totalReviews > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "AggregateRating",
      itemReviewed: { "@type": "Thing", name: h1 },
      ratingValue: avgRating.toFixed(2),
      reviewCount: totalReviews,
      bestRating: 5,
      worstRating: 1,
    });
  }

  const updatedLabel = lastUpdated
    ? new Date(lastUpdated).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : monthYear();

  return (
    <div
      className="pt-24 pb-16 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8"
      data-testid="best-list-page"
    >
      <SEO
        title={title}
        description={intro.slice(0, 155)}
        path={canonicalPath}
        keywords={`best ${categoryId || tag || ""} discord servers, top discord servers, ${categoryId ? categoryId + " discord servers" : tag ? tag + " discord servers" : "discord server list"}, discord communities`}
        schema={schemas}
      />
      <Breadcrumbs
        items={[
          { name: "Best", path: "/best/discord-servers" },
          { name: h1 },
        ]}
      />

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-neon-cyan mb-2">
          <Star className="w-3.5 h-3.5" /> Ranked list · Updated {updatedLabel}
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight">
          {h1}
        </h1>
        <p className="mt-4 text-zinc-400 text-base sm:text-lg max-w-3xl leading-relaxed">
          {intro}
        </p>

        {/* Live stats bar — real freshness signal, populated once items load */}
        {items.length > 0 && (
          <div
            className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3"
            data-testid="best-list-stats"
          >
            <StatCell
              icon={<CheckCircle2 className="w-4 h-4 text-neon-cyan" />}
              label="Servers ranked"
              value={items.length.toString()}
            />
            <StatCell
              icon={<Users className="w-4 h-4 text-neon-cyan" />}
              label="Combined members"
              value={compactNumber(totalMembers)}
            />
            <StatCell
              icon={<Star className="w-4 h-4 text-neon-cyan" />}
              label="Avg rating"
              value={avgRating ? avgRating.toFixed(1) + " / 5" : "—"}
            />
            <StatCell
              icon={<Zap className="w-4 h-4 text-neon-cyan" />}
              label="Reviews"
              value={compactNumber(totalReviews)}
            />
          </div>
        )}
      </motion.div>

      {loading ? (
        <div className="mt-12 text-sm text-zinc-500">Loading ranked list…</div>
      ) : items.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-white/10 bg-[#0A0A0F] p-8 text-center">
          <p className="text-sm text-zinc-400">
            No servers currently listed
            {categoryId ? " in this category" : tag ? " with this tag" : ""}.
            Check back soon — new servers are added daily.
          </p>
          <Link
            to="/servers"
            className="mt-4 inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-blurple hover:bg-blurple-hover text-white font-semibold"
          >
            Browse all servers <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <ol className="mt-10 space-y-3" data-testid="best-list-items">
          {items.map((s, i) => (
            <li
              key={s.id}
              className="group flex items-center gap-4 p-4 rounded-2xl border border-white/10 bg-[#0A0A0F] hover:border-blurple/40 hover:bg-blurple/[0.03] transition-all"
              data-testid={`best-item-${i + 1}`}
            >
              <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center font-heading text-lg font-black text-zinc-400 shrink-0">
                {i + 1}
              </div>
              {s.icon_url ? (
                <img
                  src={s.icon_url}
                  alt=""
                  loading="lazy"
                  className="w-14 h-14 rounded-xl border border-white/10 shrink-0"
                />
              ) : (
                <div className="w-14 h-14 rounded-xl bg-blurple/20 shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <Link
                  to={`/servers/${s.slug || s.id}`}
                  className="font-heading text-lg font-black text-white hover:text-neon-cyan transition-colors truncate block"
                >
                  {s.name}
                </Link>
                <p className="text-sm text-zinc-400 line-clamp-1 mt-0.5">
                  {s.description}
                </p>
                <div className="mt-1 flex items-center gap-3 text-xs text-zinc-500 tabular-nums">
                  <span className="inline-flex items-center gap-1">
                    <Users className="w-3 h-3" />{" "}
                    {(s.member_count || 0).toLocaleString()}
                  </span>
                  {typeof s.avg_rating === "number" && s.avg_rating > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <Star className="w-3 h-3 text-neon-cyan" />
                      {s.avg_rating.toFixed(1)}
                    </span>
                  )}
                  {(s.categories || []).slice(0, 2).map((c) => (
                    <span
                      key={c}
                      className="px-1.5 py-0.5 rounded bg-white/5 text-[10px] uppercase tracking-wider"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
              <Link
                to={`/servers/${s.slug || s.id}`}
                className="hidden sm:inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-blurple hover:bg-blurple-hover text-white text-sm font-semibold shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`best-item-cta-${i + 1}`}
              >
                View <ArrowRight className="w-4 h-4" />
              </Link>
            </li>
          ))}
        </ol>
      )}

      {/* Ranking methodology + tips — hand-written unique copy per category */}
      <section className="mt-14 prose prose-invert max-w-none prose-p:text-zinc-400 prose-headings:font-heading prose-headings:tracking-tight">
        <h2 className="font-heading text-2xl sm:text-3xl font-black">
          How this list is ranked
        </h2>
        <p>
          Every server on Serverlistr is verified via our Discord bot — no fake
          listings, no dead invites, no seven-year-old dormant communities
          padding the count. We rank
          {categoryId ? ` ${CATEGORY_META[categoryId]?.noun} ` : tag ? ` ${tag} ` : " "}
          Discord servers by their live member count pulled directly from
          Discord, weighted by recent activity: bumps in the last 24 hours,
          reviews in the last 30 days, and net member growth. The list
          refreshes automatically as members join and as server owners bump
          their listings every two hours.
        </p>

        {tips && tips.length > 0 && (
          <>
            <h2 className="font-heading text-2xl sm:text-3xl font-black mt-8">
              What to look for in a {categoryId ? CATEGORY_META[categoryId]?.noun : tag ? tag : "Discord"} server
            </h2>
            <ul className="text-zinc-400">
              {tips.map((t, i) => (
                <li key={i}>{t}</li>
              ))}
            </ul>
          </>
        )}

        <h2 className="font-heading text-2xl sm:text-3xl font-black mt-8">
          Frequently asked questions
        </h2>
        {faq.map((qa, i) => (
          <div key={i} className="mt-6">
            <h3 className="font-heading text-lg font-bold text-white">
              {qa.q}
            </h3>
            <p className="mt-2">{qa.a}</p>
          </div>
        ))}
      </section>

      {/* Cross-links to sibling best-of pages — feeds internal PageRank */}
      <section className="mt-14">
        <h2 className="font-heading text-xl sm:text-2xl font-black mb-4">
          {seedSlug
            ? `More ${seedType === "country" ? "regional" : seedType === "vibe" ? "vibe" : seedType === "game" ? "gaming" : seedType === "anime" ? "fandom" : "related"} Discord servers`
            : "Explore other best-of lists"}
        </h2>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/best/discord-servers"
            className={`text-sm font-semibold px-4 py-2 rounded-lg border transition-all ${
              !categoryId && !tag
                ? "border-neon-cyan/40 bg-neon-cyan/10 text-neon-cyan"
                : "border-white/10 bg-white/5 text-zinc-300 hover:border-white/30 hover:text-white"
            }`}
            data-testid="best-link-overall"
          >
            Top Discord servers
          </Link>
          {seedSlug
            ? siblingSeedSlugs(seedSlug, 10).map((sib) => {
                const sibSeed = getSeed(sib);
                return (
                  <Link
                    key={sib}
                    to={`/best/${sib}-discord-servers`}
                    className="text-sm font-semibold px-4 py-2 rounded-lg border border-white/10 bg-white/5 text-zinc-300 hover:border-white/30 hover:text-white transition-all"
                    data-testid={`best-link-${sib}`}
                  >
                    Best {sibSeed?.display || sib} servers
                  </Link>
                );
              })
            : ALL_CATEGORY_IDS.filter((c) => c !== categoryId).map((c) => (
                <Link
                  key={c}
                  to={`/best/${c}-discord-servers`}
                  className="text-sm font-semibold px-4 py-2 rounded-lg border border-white/10 bg-white/5 text-zinc-300 hover:border-white/30 hover:text-white transition-all"
                  data-testid={`best-link-${c}`}
                >
                  Best {prettyCategory(c)}
                </Link>
              ))}
        </div>
      </section>

      <p className="mt-10 text-xs text-zinc-600">
        {total} total{" "}
        {categoryId
          ? CATEGORY_META[categoryId]?.noun.toLowerCase()
          : tag
          ? tag
          : "Discord"}{" "}
        servers listed. Rankings update in real time as owners bump their
        listings.
      </p>
    </div>
  );
}

// --- Small helpers -------------------------------------------------

function StatCell({ icon, label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#0A0A0F] px-4 py-3">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-zinc-500">
        {icon}
        {label}
      </div>
      <div className="mt-1 font-heading text-xl font-black text-white tabular-nums">
        {value}
      </div>
    </div>
  );
}

function compactNumber(n) {
  if (!Number.isFinite(n)) return "—";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return n.toString();
}

function monthYear() {
  const d = new Date();
  return d.toLocaleString("en-US", { month: "long", year: "numeric" });
}

// --- Slug parsing --------------------------------------------------
// Parse the URL slug into structured meta. Supports three shapes:
//   "discord-servers"                            → overall top list
//   "{categoryId}-discord-servers"               → per-category top list
//   "{tag}-discord-servers"                      → tag-based landing page
// Returns null for anything else so the router redirects to /servers.

function parseSlug(slug) {
  if (!slug) return null;
  const SUFFIX = "-discord-servers";
  const year = new Date().getFullYear();

  if (slug === "discord-servers") {
    return {
      categoryId: null,
      title: `Best Discord Servers to Join in ${year}`,
      h1: `The 25 Best Discord Servers Right Now`,
      intro: OVERALL_META.intro,
      tips: OVERALL_META.tips,
      faq: OVERALL_META.faq,
    };
  }
  if (!slug.endsWith(SUFFIX)) return null;
  const cid = slug.slice(0, -SUFFIX.length);
  if (CATEGORY_META[cid]) {
    const meta = CATEGORY_META[cid];
    return {
      categoryId: cid,
      title: `Best ${meta.label} to Join in ${year}`,
      h1: `The 25 Best ${meta.label}`,
      intro: meta.intro,
      tips: meta.tips,
      faq: meta.faq,
    };
  }
  // Programmatic seed catalog — hand-curated entity-specific pages for
  // games, anime, countries, interests, and vibes. See bestListSeeds.js.
  const seed = getSeed(cid);
  if (seed) {
    const composed = composeSeedPage(seed);
    return {
      categoryId: null,
      tag: composed.tag,
      seedSlug: seed.slug,
      seedType: seed.type,
      title: composed.title,
      h1: composed.h1,
      intro: composed.intro,
      tips: composed.tips,
      faq: composed.faq,
    };
  }
  // Fallback tag-based landing — low-value last resort for unknown slugs.
  const tag = cid.replace(/[^a-z0-9-]/gi, "").toLowerCase();
  if (!tag || tag.length < 2 || tag.length > 32) return null;
  const pretty = tag.replace(/-/g, " ");
  const Title = pretty.charAt(0).toUpperCase() + pretty.slice(1);
  return {
    categoryId: null,
    tag,
    title: `Best ${Title} Discord Servers to Join in ${year}`,
    h1: `The Best ${Title} Discord Servers`,
    intro: `Discover the best ${pretty} Discord servers on Serverlistr. Every server tagged #${tag} is ranked by member count and community activity. Verified by our Discord bot — no dead invites, no fake counts, no filler.`,
    tips: [
      `Look at pinned messages in the server's welcome channel to confirm the ${pretty} focus`,
      `Larger servers offer more variety; smaller ${pretty} servers tend to feel more personal`,
      `Watch for active voice-chat and event calendars — those signal a living community`,
    ],
    faq: [
      {
        q: `How do I find more ${pretty} Discord servers?`,
        a: `Every server tagged #${tag} on Serverlistr appears on this page. Server owners choose their own tags so this list reflects real ${pretty} communities, not category best-guesses.`,
      },
      {
        q: `Are ${pretty} Discord servers on Serverlistr free to join?`,
        a: `Yes — every Discord server on Serverlistr is free to join. Click a listing and hit Join to open the invite directly in Discord.`,
      },
      {
        q: `How do I get my ${pretty} Discord server listed?`,
        a: `Sign in with Discord, add the Serverlistr bot to your server, submit your listing, and tag it "${tag}" so it appears on this page.`,
      },
    ],
  };
}
